import axios, { AxiosResponse } from 'axios'
import firebase from 'firebase'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import {
  AuthUser,
  MailVerifyAction,
  MailVerifyRequest,
  PasswordResetAction,
  PasswordResetRequest,
  PasswordSignInRequest,
} from '@app/types/src/auth'
import { AuthInfo } from '@app/types/src/auth'
import Response from '@app/types/src/schema/Response'
import { accountModule, authModule } from '.'
import Cookie from 'js-cookie'

@Module({
  name: 'auth',
  stateFactory: true,
  namespaced: true,
})
export default class AuthModule extends VuexModule {
  isSignIn: boolean = false
  email: string | null = ''
  emailVerified: boolean = false
  id: string = ''
  token: string = ''
  resendUpdateEmail: string = ''

  public get getIsSignIn(): boolean {
    return this.isSignIn
  }

  @Mutation
  public setEmail(email: string) {
    this.email = email
  }
  @Mutation
  public setResendUpdateEmail(email: string) {
    this.resendUpdateEmail = email
  }

  @Mutation
  public setIsSignIn(isSignIn: boolean) {
    this.isSignIn = isSignIn
  }
  @Mutation
  public setToken(token: string) {
    this.token = token
  }

  @Mutation
  public setAuthUser(authUser: AuthUser) {
    this.isSignIn = true
    this.id = authUser.uid
    this.email = authUser.email
    this.emailVerified = authUser.emailVerified
  }

  @Mutation
  public resetUserInfo() {
    this.isSignIn = false
    this.id = ''
    this.email = ''
    this.emailVerified = false
    this.token = ''
  }

  @Mutation
  public setId(id: string) {
    this.id = id
  }

  @Mutation
  public setEmailVerified(isVerify: boolean) {
    this.emailVerified = isVerify
  }

  @Action({ rawError: true })
  public updateAuthInfo() {
    const user = firebase.auth().currentUser
    this.setAuthUser(user as AuthUser)
  }

  @Action({ rawError: true })
  public async signOut() {
    await firebase.auth().signOut()
    this.resetUserInfo()
  }

  @Action({ rawError: true })
  public async signInWithEmailPassword(
    payload: PasswordSignInRequest
  ): Promise<{ emailVerified: boolean }> {
    await firebase
      .auth()
      .signInWithEmailAndPassword(payload.email, payload.password)
    const user: AuthUser = firebase.auth().currentUser as AuthUser
    const emailVerified = user?.emailVerified
    if (emailVerified) {
      this.setAuthUser(user)
    } else {
      // メール再送信で使用する
      authModule.setEmail(user.email!)
    }
    return { emailVerified }
  }

  @Action({ rawError: true })
  public async mailverification(
    payload: MailVerifyRequest
  ): Promise<Response<any>> {
    // メールアドレス認証はすでにアカウントを持っているユーザーなのでidTokenも送信する
    let token = await firebase?.auth().currentUser?.getIdToken()
    if (!token) {
      token = this.token
    }

    const body: Record<string, any> = {
      email: payload.email,
      create_account_flow: !!payload.createAccountFlow,
    }
    if (payload.device_id) {
      body.device_id = payload.device_id
    }

    const response = await axios.post(
      `${process.env.BASE_PATH_V2}/v1/auth/mailverification?lang=${accountModule.language}`,
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )

    const now = new Date()
    Cookie.set('cp_mailSendDate', now.toISOString(), {
      expires: new Date(now.getTime() + 60 * 1000),
      path: '/',
    })
    return response.data
  }

  @Action({ rawError: true })
  public async passwordReset(
    payload: PasswordResetRequest
  ): Promise<Response<null>> {
    const response: AxiosResponse<Response<null>> = await axios.post(
      `${process.env.BASE_PATH_V2}/v1/auth/reset-passwored?lang=${accountModule.language}`,
      {
        email: payload.email,
      }
    )
    return response.data
  }

  @Action({ rawError: true })
  public async verifyEmail(payload: MailVerifyAction) {
    await firebase.auth().applyActionCode(payload.actionCode)
    this.setEmailVerified(true)
  }

  @Action({ rawError: true })
  public async existEmail(payload: { email: string }) {
    await axios.post(`${process.env.BASE_PATH_V2}/v1/email/register/enable`, {
      email: payload.email,
    })
  }

  @Action({ rawError: true })
  public async verifyPasswordReset(
    payload: PasswordResetAction
  ): Promise<AuthInfo | void> {
    const email = await firebase
      .auth()
      .verifyPasswordResetCode(payload.actionCode)
    await firebase
      .auth()
      .confirmPasswordReset(payload.actionCode, payload.password)
    return { email, password: payload.password }
  }
}
